import { DataFormat, getPluginSettingValue, hasOptionalFieldPermission } from "imports/Utils";
import { intl } from "LocaleWrapper";
import {
	CallType,
	LaytimeCalculationInput,
	LaytimeCountingEvent,
	VolumeUnit,
	WeightUnit,
} from "__generated__/graphql";
import { Cargo, CargoAction, SalesCharter } from "types/index";
import { CallLaytimeForm } from "view/charters/types/demurrage";
import { RechargeLineFromQuery } from "view/recharge/Recharge";
import { round } from "utils/numberUtils";
import { useStoreState } from "ezStore";
import { PluginFields } from "types/enums";

export const weightFactor = (unit?: WeightUnit | null) => unit === WeightUnit.Mton ? 1 : 1000;

export const storedUnitOr = <T, >(unit: T | null | undefined, value: unknown, fallback: T): T =>
	value != null && unit ? unit : fallback;

const parseEnum = <T extends string,>(values: Record<string, T>, value: unknown): T | null =>
	Object.values(values).includes(value as T) ? value as T : null;

export const useDefaultMeasurementUnits = () => {
	const session = useStoreState(store => store.session);
	const useKg = !!hasOptionalFieldPermission(session, PluginFields.cargoAction, "kgForActuals");
	const weightUnit = parseEnum(WeightUnit, getPluginSettingValue(session, "defaultCargoMeasurementsWeightUnit")) ?? WeightUnit.Mton;
	const volumeUnit = parseEnum(VolumeUnit, getPluginSettingValue(session, "defaultCargoMeasurementsVolumeUnit")) ?? VolumeUnit.L15;

	return {
		useKg,
		weightUnit,
		volumeUnit,
	};
};

export const getActionsToCalculate = (actions: CargoAction[], index: number) => {

	const actionsToCalculate: CargoAction[] = [];
	let hitCurrent = false;

	actions.forEach(a => {
		if (!hitCurrent) {

			if (a.id === actions[index].id) {
				hitCurrent = true;
			}

			actionsToCalculate.push(a);
		}
	});

	return actionsToCalculate;
};

export const getCallTypeColor  = (callType: CallType) => {
	switch (callType) {
		case CallType.Loading:
			return "#74e12a";
		case CallType.Discharging:
			return "#f68010";
		case CallType.NitrogenPurging:
			return "#a66dd4";
		case CallType.Cleaning:
			return "#8da9c4";
		default:
			return "#f4f4f4";
	}
};

export const calculateDifference = (action: Pick<CargoAction, "measurements">, convertUnits = false) => {
	let formattedString = "";
	if (!action.measurements) {
		formattedString = "-";
	} else {
		if (action.measurements.skipperVolume && action.measurements.locationVolume) {
			formattedString = `${intl.formatMessage({ id: "CARGO_VOLUME" })}: ${DataFormat.percentage((action.measurements.skipperVolume - action.measurements.locationVolume) / action.measurements.skipperVolume * 100)}`;
		}
		if (action.measurements.skipperWeight && action.measurements.locationWeight) {
			if (formattedString.endsWith("%")) {
				formattedString += " / ";
			}

			let skipper = action.measurements.skipperWeight;
			let location = action.measurements.locationWeight;

			if (convertUnits) {
				skipper = action.measurements.skipperWeightUnit === WeightUnit.Mton ? action.measurements.skipperWeight : action.measurements.skipperWeight / 1000;
				location = action.measurements.locationWeightUnit === WeightUnit.Mton ? action.measurements.locationWeight : action.measurements.locationWeight / 1000;
			}

			formattedString += `${intl.formatMessage({ id: "CARGO_WEIGHT" })}: ${DataFormat.percentage((skipper - location) / skipper * 100)}`;
		}
	}
	return formattedString;
};

export const calculateMaxWeightAndVolume = (cargo: Cargo) => {
	let maxWeight = 0, maxVolume = 0;

	cargo.actions.reduce((acc, current) => {
		let [ weight, volume ] = acc;
		const currentWeight = current.weight || 0;
		const currentVolume = current.volume || 0;
		if (current.type === CallType.Loading) {
			weight = weight + currentWeight;
			volume = volume + currentVolume;
			if (weight > maxWeight) {
				maxWeight = weight;
			}
			if (volume > maxVolume) {
				maxVolume = volume;
			}
		} else {
			weight = weight - currentWeight;
			volume = volume - currentVolume;
		}
		return [ weight, volume ];
	}, [ 0, 0 ]);

	return [ maxWeight, maxVolume ];
};

export const calculateNetWeightAndVolume = (actions: CargoAction[]) => {
	return actions.reduce((acc, current) => {
		let [ netWeight, netVolume ] = acc;
		const currentWeight = current.weight || 0;
		const currentVolume = current.volume || 0;
		netWeight = netWeight + currentWeight * (current.type == CallType.Loading ? 1 : -1);
		netVolume = netVolume + currentVolume * (current.type == CallType.Loading ? 1 : -1);
		return [ round(netWeight, 3), round(netVolume, 3) ];
	}, [ 0, 0 ]);
};

export const cargoActualsComparison = (shipSide: number | null | undefined, landSide: number | null | undefined) => {
	if (!shipSide || !landSide) {
		return null;
	}

	return {
		percentage: (shipSide - landSide) / shipSide * 100,
		absolute: shipSide - landSide,
	};
};


export const cargoActionBLComparison = (action: Pick<CargoAction, "measurements"> | Pick<RechargeLineFromQuery, "measurements">, field: "WEIGHT" | "VOLUME") => {
	let skipperValue, locationValue, unit;
	switch (field) {
		case "WEIGHT":
			skipperValue = (action.measurements?.skipperWeight ?? 0) * weightFactor(action.measurements?.skipperWeightUnit);
			locationValue = (action.measurements?.locationWeight ?? 0) * weightFactor(action.measurements?.locationWeightUnit);

			if (action.measurements?.locationWeightUnit !== action.measurements?.skipperWeightUnit) {
				skipperValue = `${skipperValue} ${intl.formatMessage({ id: `UNIT_${action.measurements?.skipperWeightUnit}` })}`;
				locationValue = `${locationValue} ${intl.formatMessage({ id: `UNIT_${action.measurements?.locationWeightUnit}` })}`;
				unit = "";
			} else {
				unit = intl.formatMessage({ id: `UNIT_${action.measurements?.locationWeightUnit}` });
			}
			break;
		case "VOLUME":
			skipperValue = action.measurements?.skipperVolume ?? 0;
			locationValue = action.measurements?.locationVolume ?? 0;

			if (action.measurements?.locationVolumeUnit !== action.measurements?.skipperVolumeUnit) {
				skipperValue = `${skipperValue} ${intl.formatMessage({ id: `UNIT_${action.measurements?.skipperVolumeUnit}` })}`;
				locationValue = `${locationValue} ${intl.formatMessage({ id: `UNIT_${action.measurements?.locationVolumeUnit}` })}`;
				unit = "";
			} else {
				unit = intl.formatMessage({ id: `UNIT_${action.measurements?.locationVolumeUnit}` });
			}
			break;
	}

	if (!skipperValue && !locationValue ) {
		return <></>;
	}

	if (!skipperValue) {
		skipperValue = "-";
	}

	if (!locationValue) {
		locationValue = "-";
	}

	return <span style={{ color: skipperValue !== locationValue ? "red" : "initial" }}>
		{skipperValue} / {locationValue} {unit}
	</span>;
};

export const getLaytimePropertiesInput = (callId: number, data: CallLaytimeForm): LaytimeCalculationInput => {
	// Extract event and status from the combined option
	const startEvent = data.startLaytimeCountingOption?.type === "status"
		? LaytimeCountingEvent.Status
		: data.startLaytimeCountingOption?.type === "event"
			? data.startLaytimeCountingOption.event
			: undefined;

	const endEvent = data.endLaytimeCountingOption?.type === "status"
		? LaytimeCountingEvent.Status
		: data.endLaytimeCountingOption?.type === "event"
			? data.endLaytimeCountingOption.event
			: undefined;

	const startStatus = data.startLaytimeCountingOption?.type === "status"
		? data.startLaytimeCountingOption.statusChange.id
		: undefined;

	const endStatus = data.endLaytimeCountingOption?.type === "status"
		? data.endLaytimeCountingOption.statusChange.id
		: undefined;

	const startLabel = data.startLaytimeCountingOption?.type === "event" && data.startLaytimeCountingOption.event === LaytimeCountingEvent.Custom
		? data.startLaytimeCountingLabel
		: undefined;

	const endLabel = data.endLaytimeCountingOption?.type === "event" && data.endLaytimeCountingOption.event === LaytimeCountingEvent.Custom
		? data.endLaytimeCountingLabel
		: undefined;

	return {
		callId: callId,
		startEvent: startEvent,
		endEvent: endEvent,
		startTimestamp: data.startLaytimeCountingTimestamp,
		endTimestamp: data.endLaytimeCountingTimestamp,
		startStatus: startStatus,
		endStatus: endStatus,
		startLabel: startLabel,
		endLabel: endLabel,
	};
};

type CargoActionForBlDate =  { type: CallType; start?: number | null; call?: { atd?: number | null } | null };

export const getCargoBlDate = (actions: CargoActionForBlDate[]) => {
	const latestLoadingAction = actions.filter(action => action.type === CallType.Loading).reduce<CargoActionForBlDate | null>((latest, current) => {
		const currentTimestamp =  current.start;
		if (!currentTimestamp) {
			return latest;
		}

		if (!latest) {
			return current;
		}

		const latestTimestamp = latest.start;
		if (!latestTimestamp || currentTimestamp > latestTimestamp) {
			return current;
		}

		return latest;
	},
	null);

	return latestLoadingAction?.call?.atd;
};

export const validateActionTypeSequence = (
	actions: CargoAction[],
	index: number,
	actionType: CallType,
	isPurgingAllowed: boolean,
	isCleaningAllowed: boolean,
): boolean => {
	const totalElements = actions.length;
	if (totalElements === 0) {
		return true;
	}

	if (actionType === CallType.NitrogenPurging && !isPurgingAllowed) {
		return false;
	}

	if (actionType === CallType.Cleaning && !isCleaningAllowed) {
		return false;
	}

	const sequence = actions.map(x => x.type ?? CallType.Loading);
	sequence[index] = actionType;

	const getIndexByType = (type: CallType): number[] => {
		const result: number[] = [];
		sequence.forEach((x, i) => {
			if (x === type) {
				result.push(i);
			}
		});
		return result;
	};

	const loadingActions = getIndexByType(CallType.Loading);
	const dischargingActions = getIndexByType(CallType.Discharging);
	const purgingActions = getIndexByType(CallType.NitrogenPurging);
	const cleaningActions = getIndexByType(CallType.Cleaning);

	// sequence must contain at least one of both loading and discharging
	if (loadingActions.length === 0 || dischargingActions.length === 0) {
		return false;
	}

	const firstLoadingAction = loadingActions[0];
	const lastLoadingAction = loadingActions[loadingActions.length - 1];

	const firstDischargingAction = dischargingActions[0];
	const lastDischargingAction = dischargingActions[dischargingActions.length - 1];

	if (purgingActions.length > 0) {
		// purging can only happen at the very beginning
		if (purgingActions.some(i => i !== 0)) {
			return false;
		}
		if (purgingActions[0] > firstLoadingAction) {
			return false;
		}
	}

	if (cleaningActions.length > 0) {
		// cleaning can only happen at the very end
		if (cleaningActions.some(i => i !== totalElements - 1)) {
			return false;
		}
		if (cleaningActions[0] < lastDischargingAction) {
			return false;
		}
	}

	// cannot start with discharging
	if (firstDischargingAction < firstLoadingAction) {
		return false;
	}

	// cannot end with loading
	if (lastLoadingAction > lastDischargingAction) {
		return false;
	}

	return true;
};

export const laytimeTextToMinutes = (text?: string) => {
	if (text == null) {
		return undefined;
	}

	const trimmed = text.trim();
	const mins = /^(\d+):([0-5]\d)$/.exec(trimmed);
	if (mins == null) {
		return undefined;
	}

	const hours = Number(mins[1]);
	const minutes = Number(mins[2]);
	if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
		return undefined;
	}

	return hours * 60 + minutes;
};

export const normalizeLaytimeText = (text: string, autoColon = false): string => {
	let value = text.replace(/[^\d:]/g, "");

	const i = value.indexOf(":");
	if (i !== -1) {
		value = value.slice(0, i + 1) + value.slice(i + 1).replace(/:/g, "");
	}

	if (autoColon && !value.includes(":")) {
		const digits = value.replace(/\D/g, "");
		if (digits.length >= 3) {
			value = `${digits.slice(0, -1)}:${digits.slice(-1)}`;
		} else {
			value = digits;
		}
	}

	if (value.includes(":")) {
		const [ h, m = "" ] = value.split(":");
		value = `${h}:${m.slice(0, 2)}`;
	}

	return value;
};

export const laytimeMinutesToHHMM = (minutes?: number | null) => {
	if (minutes == null) {
		return "";
	}
	const hh = Math.floor(minutes / 60);
	const mm = minutes % 60;
	return `${hh}:${String(mm).padStart(2, "0")}`;
};

export const laytimeHHMMToMinutes = (value?: string) => {
	if (!value) {
		return undefined;
	}

	const [ h, m ] = value.split(":");
	const hh = parseInt(h ?? "", 10);
	const mm = parseInt(m ?? "", 10);

	if (!Number.isFinite(hh) || !Number.isFinite(mm)) {
		return undefined;
	}
	if (hh < 0) {
		return undefined;
	}
	if (mm < 0 || mm > 59) {
		return undefined;
	}

	return hh * 60 + mm;
};

export const LAYTIME_MASK = [
	...Array.from({ length: 10 }, () => /\d/),
	":",
	/[0-5]/,
	/\d/,
];

export const charterHasLaytimeActionWith = (charter: SalesCharter, actionType: CallType) => {
	return charter.cargos?.flatMap(c => c.actions)
		.filter(a => a.includedForLaytimeCalculation)
		.some(a => a.type == actionType);
};
